import type { InputMethod, NavigationDirection } from "../../input";
import { getAdjacentId } from "../../input";
import {
  completeChoiceMatchProgress,
  createChoiceMatchProgressState,
  createNeutralRetryProgress,
  requestPromptReplay,
  type ChoiceMatchProgressState
} from "./progress";
import type { ChoicePayload } from "./schemas";

export interface ChoiceActivityState extends ChoiceMatchProgressState {
  readonly mode: ChoicePayload["kind"];
  readonly focusedChoiceId: string | null;
  readonly selectedChoiceIds: readonly string[];
  readonly lastInputMethod: InputMethod | null;
}

export type ChoiceActivityAction =
  | {
      readonly type: "move-focus";
      readonly direction: NavigationDirection;
    }
  | {
      readonly type: "select-choice";
      readonly choiceId: string;
      readonly inputMethod: InputMethod;
    }
  | { readonly type: "activate-focused" }
  | { readonly type: "submit" }
  | { readonly type: "replay-prompt" };

export function getChoiceKeyboardOrder(
  payload: ChoicePayload
): readonly string[] {
  return payload.choices.map(({ id }) => id);
}

export function createChoiceActivityState(
  payload: ChoicePayload
): ChoiceActivityState {
  return {
    ...createChoiceMatchProgressState(),
    mode: payload.kind,
    focusedChoiceId: null,
    selectedChoiceIds: [],
    lastInputMethod: null
  };
}

function isKnownChoice(payload: ChoicePayload, choiceId: string): boolean {
  return payload.choices.some(({ id }) => id === choiceId);
}

function selectChoice(
  payload: ChoicePayload,
  state: ChoiceActivityState,
  choiceId: string,
  inputMethod: InputMethod
): ChoiceActivityState {
  if (state.status === "complete" || !isKnownChoice(payload, choiceId)) {
    return state;
  }

  if (payload.kind === "single-choice") {
    return {
      ...state,
      feedback: null,
      focusedChoiceId: choiceId,
      selectedChoiceIds: [choiceId],
      lastInputMethod: inputMethod
    };
  }

  const selectedIds = new Set(state.selectedChoiceIds);

  if (selectedIds.has(choiceId)) {
    selectedIds.delete(choiceId);
  } else {
    selectedIds.add(choiceId);
  }

  return {
    ...state,
    feedback: null,
    focusedChoiceId: choiceId,
    selectedChoiceIds: getChoiceKeyboardOrder(payload).filter((id) =>
      selectedIds.has(id)
    ),
    lastInputMethod: inputMethod
  };
}

function isCorrectSelection(
  payload: ChoicePayload,
  selectedChoiceIds: readonly string[]
): boolean {
  const correctChoiceIds =
    payload.kind === "single-choice"
      ? [payload.correctChoiceId]
      : payload.correctChoiceIds;

  return (
    selectedChoiceIds.length === correctChoiceIds.length &&
    correctChoiceIds.every((id) => selectedChoiceIds.includes(id))
  );
}

export function reduceChoiceActivity(
  payload: ChoicePayload,
  state: ChoiceActivityState,
  action: ChoiceActivityAction
): ChoiceActivityState {
  if (payload.kind !== state.mode) {
    return state;
  }

  switch (action.type) {
    case "move-focus": {
      if (state.status === "complete") {
        return state;
      }

      const focusedChoiceId = getAdjacentId(
        getChoiceKeyboardOrder(payload),
        state.focusedChoiceId,
        action.direction
      );

      if (focusedChoiceId === null) {
        return state;
      }

      return {
        ...state,
        focusedChoiceId,
        lastInputMethod: "keyboard"
      };
    }
    case "select-choice":
      return selectChoice(
        payload,
        state,
        action.choiceId,
        action.inputMethod
      );
    case "activate-focused":
      return state.focusedChoiceId === null
        ? state
        : selectChoice(
            payload,
            state,
            state.focusedChoiceId,
            "keyboard"
          );
    case "submit": {
      if (
        state.status === "complete" ||
        state.selectedChoiceIds.length === 0
      ) {
        return state;
      }

      if (isCorrectSelection(payload, state.selectedChoiceIds)) {
        return {
          ...state,
          ...completeChoiceMatchProgress(state, 1)
        };
      }

      return {
        ...state,
        ...createNeutralRetryProgress(state),
        selectedChoiceIds:
          payload.kind === "single-choice"
            ? []
            : state.selectedChoiceIds
      };
    }
    case "replay-prompt":
      return {
        ...state,
        ...requestPromptReplay(state)
      };
  }
}
